<template>
  <input ref="inputRef" :value="modelValue" :type="type" :autocomplete="autocomplete" :disabled="disabled"
    :min="min" :max="max" :maxlength="maxlength" :placeholder="placeholder" :required="required"
    :inputmode="numericOnly ? 'numeric' : undefined" :class="inputClasses" @input="handleInput"
    @focus="emit('focus', $event)" @blur="handleBlur" @keydown="emit('keydown', $event)" />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

type InputType = 'text' | 'search' | 'email' | 'tel' | 'number' | 'date' | 'url';
type InputVariant = 'default' | 'form';

const props = withDefaults(defineProps<{
  modelValue?: string;
  type?: InputType;
  autocomplete?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  defaultValue?: string | number;
  numericOnly?: boolean;
  maxlength?: number | string;
  placeholder?: string;
  required?: boolean;
  variant?: InputVariant;
}>(), {
  modelValue: '',
  type: 'text',
  autocomplete: undefined,
  disabled: false,
  min: undefined,
  max: undefined,
  defaultValue: undefined,
  numericOnly: false,
  maxlength: undefined,
  placeholder: '',
  required: false,
  variant: 'default'
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  input: [event: Event];
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
  keydown: [event: KeyboardEvent];
}>();

const inputRef = ref<HTMLInputElement | null>(null);

const inputClasses = computed(() => [
  'ui-input',
  `ui-input--${props.variant}`,
  {
    'ui-input--disabled': props.disabled
  }
]);

function focus(): void {
  inputRef.value?.focus();
}

function handleInput(event: Event): void {
  const target = event.target as HTMLInputElement;
  const value = props.numericOnly ? target.value.replace(/\D/g, '') : target.value;
  if (value !== target.value) {
    target.value = value;
  }
  emit('update:modelValue', value);
  emit('input', event);
}

function handleBlur(event: FocusEvent): void {
  const target = event.target as HTMLInputElement;
  if (props.numericOnly) {
    const fallback = String(props.defaultValue ?? '');
    const numericValue = Number.parseInt(target.value, 10);
    const hasValue = target.value.length > 0 && Number.isFinite(numericValue);
    const boundedValue = hasValue
      ? Math.min(props.max ?? Number.POSITIVE_INFINITY, Math.max(props.min ?? Number.NEGATIVE_INFINITY, numericValue))
      : fallback;

    const nextValue = boundedValue === '' ? '' : String(boundedValue);
    if (nextValue !== target.value) {
      target.value = nextValue;
      emit('update:modelValue', nextValue);
    }
  }
  emit('blur', event);
}

onMounted(() => {
  if (props.modelValue || props.defaultValue === undefined) return;
  emit('update:modelValue', String(props.defaultValue));
});

defineExpose({
  focus,
  inputRef
});
</script>
